import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Eye, EyeOff, Lock, ShieldCheck, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { FadeIn, SlideIn } from '../components/animations';
import toast from 'react-hot-toast';

interface ValidationErrors {
  otp?: string;
  password?: string;
  confirmPassword?: string;
}

const validatePassword = (password: string): string | null => {
  if (password.length < 6) {
    return 'Password must be at least 6 characters';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one number';
  }
  return null;
};

const ResetPassword: React.FC = () => {
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!otp.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }

    if (!newPassword) {
      newErrors.password = 'Password is required';
    } else {
      const passwordError = validatePassword(newPassword);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field: keyof ValidationErrors) => {
    setTouched({ ...touched, [field]: true });
    validate();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ otp: true, password: true, confirmPassword: true });

    if (!validate()) {
      toast.error('Please fix the errors above');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword({ email, otp, newPassword });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to reset password';
      setErrors({ otp: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getFieldClasses = (field: keyof ValidationErrors) => {
    if (!touched[field]) return 'border-gray-300';
    return errors[field] ? 'border-red-500' : 'border-green-500';
  };

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Access</h2>
          <p className="text-gray-600 mb-6">Please request a password reset first.</p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Go to Forgot Password
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex">
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
          <FadeIn>
            <div className="text-center max-w-md">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Password Reset!</h2>
              <p className="text-gray-600 mb-8">
                Your password has been successfully reset. You can now login with your new password.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Login
              </Link>
            </div>
          </FadeIn>
        </div>
        <div className="hidden lg:flex flex-1 bg-gradient-to-br from-green-500 to-teal-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10 flex flex-col justify-center px-12 text-white">
            <FadeIn delay={0.2}>
              <h2 className="text-4xl font-bold mb-6">All Done!</h2>
              <p className="text-xl text-green-100 mb-8 leading-relaxed">
                Your account is now secure with your new password. You can continue using all features of BSAF.
              </p>
            </FadeIn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-md w-full py-12">
          <FadeIn>
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center mb-6">
                <img src="/images/logo.png" alt="BSAF" className="h-16 w-auto mx-auto" />
              </Link>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
              <p className="text-gray-600">Enter the OTP sent to your email</p>
            </div>
          </FadeIn>

          <SlideIn direction="up" delay={0.1}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {(errors.otp || errors.password || errors.confirmPassword) && Object.keys(touched).length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
                  {errors.otp && <p className="flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {errors.otp}</p>}
                  {errors.password && <p className="flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {errors.password}</p>}
                  {errors.confirmPassword && <p className="flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {errors.confirmPassword}</p>}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  OTP sent to: <strong>{email}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">OTP Code *</label>
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  onBlur={() => handleBlur('otp')}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-center text-2xl tracking-widest ${getFieldClasses('otp')}`}
                  placeholder="000000"
                />
                {touched.otp && !errors.otp && otp.length === 6 && (
                  <p className="mt-1 text-sm text-green-500 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> OTP verified
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password *</label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${touched.password && !errors.password ? 'text-green-500' : 'text-gray-400'}`} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onBlur={() => handleBlur('password')}
                    className={`w-full pl-11 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${getFieldClasses('password')}`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {touched.password && !errors.password && newPassword && (
                  <p className="mt-1 text-sm text-green-500 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Password looks good
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${touched.confirmPassword && !errors.confirmPassword ? 'text-green-500' : 'text-gray-400'}`} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => handleBlur('confirmPassword')}
                    className={`w-full pl-11 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${getFieldClasses('confirmPassword')}`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {touched.confirmPassword && !errors.confirmPassword && confirmPassword && newPassword === confirmPassword && (
                  <p className="mt-1 text-sm text-green-500 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Passwords match
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    Reset Password
                    <ShieldCheck className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          </SlideIn>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.2 }}></div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <FadeIn delay={0.2}>
            <h2 className="text-4xl font-bold mb-6">Create New Password</h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Enter the 6-digit code sent to your email and create a strong password to secure your account.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🔐</span>
                </div>
                <span className="text-lg">Use a strong password</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">✉️</span>
                </div>
                <span className="text-lg">Check your email for OTP</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">✓</span>
                </div>
                <span className="text-lg">Quick and secure process</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
